import type { ActionId } from "./actions.ts";
import type { KeyTable } from "./page.ts";

/**
 * The press a held key is asked about as, reused.
 */
const PRESS: { key: string; code: string } = { key: "", code: "" };

/**
 * The physical keys the game holds down (P1.9-T50b and P1.9-T174; ADR-061's amendments "Move joins the key table",
 * point 8, and "every key rebindable but Escape", point 3): Move's two sets and Sprint are read from them once for each
 * input. A key goes down only while the game owns the keyboard and comes up whoever owns
 * it; every key is let go where Phaser's own key objects are, when the keyboard changes owner and when the window
 * loses focus, so a direction never stays held across chat, a modal or an alt-tab.
 */
export class HeldKeys {
  private readonly down = new Set<string>();

  /**
   * Hears a physical key go down or up.
   *
   * @param code The `KeyboardEvent.code` value; an empty or absent one is no physical key and is ignored.
   * @param down Whether it went down.
   * @param owned Whether the game owned the keyboard: a key pressed while it did not is not held.
   */
  public key(code: string | undefined, down: boolean, owned: boolean): void {
    if (code === undefined || code === "") {
      return;
    }
    if (!down) {
      this.down.delete(code);
    } else if (owned) {
      this.down.add(code);
    }
  }

  /**
   * Whether a physical key is held.
   *
   * @param code The `KeyboardEvent.code` value.
   * @returns True while it is down.
   */
  public held(code: string | null): boolean {
    return code !== null && this.down.has(code);
  }

  /**
   * Whether any key held is an action's, by the key table (P1.9-T174): its own key or one that answers for it, a right
   * Shift for Sprint on the left. Reading allocates nothing.
   *
   * @param keys The key table.
   * @param action The action.
   * @returns True while a key of the action is down.
   */
  public heldAs(keys: Pick<KeyTable, "actionOf">, action: ActionId): boolean {
    for (const code of this.down) {
      PRESS.code = code;
      if (keys.actionOf(PRESS) === action) {
        return true;
      }
    }
    return false;
  }

  /**
   * Lets go of every key.
   */
  public release(): void {
    this.down.clear();
  }
}
